import { BaseFeature } from '../../core/base-feature.js';

/**
 * GoFishFeature - Frontend feature for Go Fish game
 *
 * Controls the desktop overlay game from the frontend.
 * Receives full game state including desktop's hand and draw pile (intel advantage).
 * Can lie when asked for cards by the desktop.
 */
export class GoFishFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.currentState = null;
        this.stateCallbacks = new Set();
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'go-fish',
            name: 'Go Fish',
            version: 'v1.0',
            tier: 'external',
            capabilities: ['start', 'frontend_ask', 'respond_to_ask', 'get_state', 'end'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[GoFish] Initializing...');
        console.log('[GoFish] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                this.currentState = payload;
                this.notifyStateListeners(payload);
                break;

            case 'start_response':
            case 'frontend_ask_response':
            case 'respond_to_ask_response':
            case 'get_state_response':
            case 'end_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this._pendingRequests.has(originalId)) {
                    const { resolve } = this._pendingRequests.get(originalId);
                    this._pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[GoFish] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this._pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);

            this._pendingRequests.set(messageId, {
                resolve: (result) => {
                    clearTimeout(timeout);
                    resolve(result);
                },
                reject
            });
        });
    }

    startGame() {
        return this._sendRequest('start');
    }

    askForRank(rank) {
        return this._sendRequest('frontend_ask', { rank });
    }

    respondToAsk(honest) {
        return this._sendRequest('respond_to_ask', { honest });
    }

    getState() {
        return this._sendRequest('get_state');
    }

    endGame() {
        return this._sendRequest('end');
    }

    // Event subscriptions for React
    onStateUpdate(callback) {
        this.stateCallbacks.add(callback);
        return () => this.stateCallbacks.delete(callback);
    }

    notifyStateListeners(data) {
        for (const callback of this.stateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[GoFish] Error in state callback:', error);
            }
        }
    }

    getCurrentState() {
        return this.currentState;
    }

    async cleanup() {
        console.log('[GoFish] Cleaning up...');
        this.currentState = null;
        this.stateCallbacks.clear();
        for (const [, { reject }] of this._pendingRequests) {
            reject(new Error('Feature cleanup'));
        }
        this._pendingRequests.clear();
    }
}
